import './chat-message-create-file-modal.scss';

import { useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import { toUpload } from '../../../../data/GraphQL/api/format';
import {
  ConversationChatMessageItemDataFragment,
  ConversationItemDataFragment,
  CreateConversationChatFileMessageItemDocument,
} from '../../../../data/types/api/graphql';
import {
  FileItemData,
  FileSizeDocument,
  FileTypeImage,
  FileTypePdf,
  formatFileSize,
  getThumbnail,
} from '../../../../data/utils/upload.utils';
import { AlertMessageType } from '../../../alert-message/alert-message';
import { Button, ButtonIcon, ButtonType } from '../../../button/button';
import { InputFieldController } from '../../../form/controllers/input-field';
import { FormRow } from '../../../form/form';
import { InputUpload } from '../../../form/input/input-upload';
import { CustomFormattedMessage } from '../../../intl/custom-formatted-message';
import { Modal, ModalWidth } from '../../../modal/modal';
import { ModalPrompt } from '../../../modal/modal-prompt';
import { useChatMessageCreateFileModalForm } from './form/use-chat-message-create-file-modal-form';

type ChatMessageCreateFileModalProps = {
  conversationItem: ConversationItemDataFragment | null | undefined;
  onSaved?: (message: ConversationChatMessageItemDataFragment) => void;
  onCancel?: () => void;
  onClose?: () => void;
};

export function ChatMessageCreateFileModal({
  conversationItem,
  onSaved,
  onCancel,
  onClose,
}: ChatMessageCreateFileModalProps) {
  const intl = useIntl();

  const { showAlertMessage } = useAppContext();

  const { form, formIsValid } = useChatMessageCreateFileModalForm();

  const files = form.watch('files');

  const onHandleFile = useCallback(
    (value: FileItemData[]) => {
      form.setValue('files', [...files, ...value], {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [form, files]
  );

  const onDeleteFile = useCallback(
    (value: FileItemData) => {
      form.setValue(
        'files',
        files.filter(option => option?.name !== value.name),
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    },
    [form, files]
  );

  const [
    createConversationChatFileMessageItem,
    { loading: createConversationChatFileMessageItemLoading },
  ] = useMutation(CreateConversationChatFileMessageItemDocument);

  const onHandleSubmit = useCallback(() => {
    form.handleSubmit(async () => {
      const values = form.getValues();

      try {
        const result = await createConversationChatFileMessageItem({
          variables: {
            conversationId: conversationItem?.id,
            text: values.text,
            files: values.files.map(option => ({
              fileName: option?.name,
              base64Data: toUpload(option?.base64),
            })),
            totalCount: values.files.length,
          },
        });

        if (result.data?.messageFilesCreate?.message) {
          onSaved?.(result.data?.messageFilesCreate?.message);

          onClose?.();
        } else {
          showAlertMessage({
            type: AlertMessageType.ERROR,
            message: intl.formatMessage({
              id: 'common.error',
            }),
          });
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    })();
  }, [
    intl,
    form,
    conversationItem,
    createConversationChatFileMessageItem,
    showAlertMessage,
    onSaved,
    onClose,
  ]);

  const [cancelPromptOpen, setCancelPromptOpen] = useState<boolean>(false);

  const onHandleCancel = useCallback(
    (confirmed: boolean = false) => {
      if (!confirmed && form.formState.isDirty) {
        setCancelPromptOpen(true);
      } else {
        onCancel?.();

        onClose?.();
      }
    },
    [form.formState.isDirty, onCancel, onClose]
  );

  return (
    <>
      <Modal
        title={
          <CustomFormattedMessage id="conversation.chat-message-create.file-upload.title" />
        }
        width={ModalWidth.LARGE}
        onClose={onHandleCancel}
        buttons={
          <>
            <Button
              type={ButtonType.SECONDARY}
              inverted
              onClick={() => onHandleCancel(false)}
            >
              <CustomFormattedMessage id="common.cancel" />
            </Button>

            <Button
              icon={
                createConversationChatFileMessageItemLoading
                  ? ButtonIcon.LOADING
                  : ButtonIcon.PAPERPLANE
              }
              disabled={
                !formIsValid || createConversationChatFileMessageItemLoading
              }
              onClick={onHandleSubmit}
            >
              <CustomFormattedMessage id="conversation.chat-message-create.save" />
            </Button>
          </>
        }
      >
        <FormProvider {...form}>
          <FormRow>
            <InputUpload
              name="upload"
              label={<CustomFormattedMessage id="common.file" />}
              fileTypes={[...FileTypeImage, ...FileTypePdf]}
              fileSize={FileSizeDocument}
              multiple
              onChange={value => onHandleFile(value as FileItemData[])}
            />
          </FormRow>

          {files.length > 0 && (
            <div className="chat-message-uploads-overview">
              {files.map(option => {
                const thumbnail = getThumbnail(option?.base64);

                return (
                  <div
                    key={option?.name}
                    className="chat-message-uploads-overview__item"
                  >
                    <div className="chat-message-upload">
                      <div className="chat-message-upload__preview">
                        <div className="thumbnail">
                          {thumbnail && (
                            <img src={thumbnail} alt={option?.name} />
                          )}
                        </div>
                      </div>
                      <div className="chat-message-upload__content">
                        <h2>
                          <CustomFormattedMessage id="common.file-upload.file-name" />
                        </h2>
                        <p>{option?.name}</p>
                      </div>
                      <div className="chat-message-upload__size">
                        <h2>
                          <CustomFormattedMessage id="common.file-upload.file-size" />
                        </h2>
                        <p>{formatFileSize(option?.size ?? 0, 'MB')}</p>
                      </div>
                      <div className="chat-message-upload__action">
                        <Button
                          type={ButtonType.TRANSPARENT}
                          icon={ButtonIcon.MINUS}
                          onClick={() => onDeleteFile(option!)}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <FormRow>
            <InputFieldController
              name="text"
              label={
                <CustomFormattedMessage id="conversation.chat-message-create.file-upload.message" />
              }
            />
          </FormRow>
        </FormProvider>
      </Modal>

      {cancelPromptOpen && (
        <ModalPrompt
          onConfirm={() => onHandleCancel(true)}
          onClose={() => setCancelPromptOpen(false)}
        >
          <p>
            <CustomFormattedMessage id="common.confirm-not-saved-changes" />
          </p>
        </ModalPrompt>
      )}
    </>
  );
}
